/**
 * An event stream that ANSWERS an ordinary HTTP request.
 *
 * `GET /events` from a normal HTTP tab used to spin for 30 s and fail: the body of a
 * `text/event-stream` response never ends. Now the desktop backend answers with the head
 * and streams the body (`src-tauri/src/http_stream.rs`); this registry holds what arrives,
 * per TAB — like `liveConnections.js` holds a WebSocket — so a tab switch loses nothing and
 * closing the tab stops the stream.
 *
 * Rows are in `ui/StreamLogTable`'s shape from the start (a row that never changes is never
 * rendered again).
 */
import { useSyncExternalStore } from 'react';
import { SSEParser } from './sseParser';
import { MAX_LOG_MESSAGES } from './liveConnections';

const EMPTY = Object.freeze({ status: 'idle', entries: [], dropped: 0, startedAt: null, endedAt: null });

const streams = new Map(); // tab id → { snapshot, streamId, parser, stop }
const listeners = new Set();
let counter = 0;

const emit = () => listeners.forEach((fn) => fn());
const keyOf = (tabId) => (tabId === null || tabId === undefined ? null : String(tabId));
const rowId = () => `hes-${Date.now()}-${++counter}`;

function patch(entry, change) {
  entry.snapshot = { ...entry.snapshot, ...change }; // a NEW object: useSyncExternalStore compares by identity
  emit();
}

function append(entry, rows) {
  if (rows.length === 0) return;
  const all = [...entry.snapshot.entries, ...rows];
  const overflow = Math.max(0, all.length - MAX_LOG_MESSAGES);
  patch(entry, { entries: overflow ? all.slice(overflow) : all, dropped: entry.snapshot.dropped + overflow });
}

const eventRow = (event, data, detail = null) => ({ id: rowId(), kind: 'event', event, data, detail, timestamp: Date.now() });

/**
 * Start collecting for a tab. Returns what `sendRequest({ stream })` needs: the id the
 * backend streams under, and the three callbacks. Whatever the tab streamed before is
 * stopped and forgotten — one request, one response.
 *
 * @param {string} tabId
 * @param {{ url?: string, cancel?: (streamId: string) => void }} [options]
 */
export function beginHttpEventStream(tabId, { url = '', cancel } = {}) {
  const key = keyOf(tabId);
  if (key === null) return null;
  closeHttpEventStream(key);

  const streamId = `${key}-${Date.now()}-${++counter}`;
  const entry = { snapshot: { ...EMPTY, status: 'pending' }, streamId, parser: new SSEParser(), stop: null, early: [] };
  streams.set(key, entry);
  const mine = () => streams.get(key) === entry;
  entry.stop = () => cancel?.(streamId);

  const rowsOf = (text) => entry.parser.feed(text).map((ev) => ({
    id: rowId(), kind: 'message', direction: 'in', timestamp: Date.now(),
    tag: ev.event || 'message', data: ev.data,
  }));
  const end = (status, row) => { append(entry, [row]); patch(entry, { status, endedAt: Date.now() }); };
  const ended = () => end('ended', eventRow('disconnected', 'The server ended the stream', { url }));
  const broke = (message) => end('error', eventRow('error', 'The connection broke', { url, message: String(message || '') }));

  // The backend forwards the body as soon as the head is in — the first events can arrive
  // BEFORE the request's own answer does. They wait in `early` until `onOpen`.
  return {
    id: streamId,
    /** The head arrived and it IS an event stream. `false`: nobody wants it any more (the tab
     *  closed or sent again while the request was in flight) — the transport stops it. */
    onOpen: () => {
      if (!mine()) return false;
      patch(entry, { status: 'streaming', startedAt: Date.now() });
      append(entry, [eventRow('connected', url ? `Connected to ${url}` : 'Connected', { url })]);
      entry.early.splice(0).forEach((step) => step());
      return true;
    },
    onChunk: (text) => {
      if (!mine()) return;
      if (entry.snapshot.status === 'pending') entry.early.push(() => append(entry, rowsOf(text)));
      else if (entry.snapshot.status === 'streaming') append(entry, rowsOf(text));
    },
    onDone: () => {
      if (!mine()) return;
      if (entry.snapshot.status === 'pending') entry.early.push(ended);
      else if (entry.snapshot.status === 'streaming') ended();
    },
    onError: (message) => {
      if (!mine()) return;
      if (entry.snapshot.status === 'pending') entry.early.push(() => broke(message));
      else if (entry.snapshot.status === 'streaming') broke(message);
    },
    /** The response was NOT a stream (or the request failed): nothing to keep. */
    onNotAStream: () => { if (mine()) { streams.delete(key); emit(); } },
  };
}

/** The user's Stop: a real abort on the backend, and a row that says so. */
export function stopHttpEventStream(tabId) {
  const entry = streams.get(keyOf(tabId));
  if (!entry || entry.snapshot.status !== 'streaming') return;
  entry.stop?.();
  append(entry, [eventRow('disconnected', 'Stopped', { message: 'Closed from Apiboo.' })]);
  patch(entry, { status: 'stopped', endedAt: Date.now() });
}

/**
 * The tab is gone, or sends again: stop and forget. With `streamId`: only if the tab's stream
 * still IS that one — a cancelled request that answers late must not take down the stream of
 * the request that was sent after it.
 */
export function closeHttpEventStream(tabId, streamId) {
  const key = keyOf(tabId);
  const entry = streams.get(key);
  if (!entry) return;
  if (streamId !== undefined && entry.streamId !== streamId) return;
  streams.delete(key);
  if (entry.snapshot.status === 'streaming' || entry.snapshot.status === 'pending') entry.stop?.();
  emit();
}

export function clearHttpEventStreamLog(tabId) {
  const entry = streams.get(keyOf(tabId));
  if (entry) patch(entry, { entries: [], dropped: 0 });
}

/** Keep only what belongs to a tab that is still open. */
export function pruneHttpEventStreams(openTabIds) {
  const open = new Set([...openTabIds].map(String));
  [...streams.keys()].filter((key) => !open.has(key)).forEach((key) => closeHttpEventStream(key)); // not `forEach(close…)`: the index would arrive as `streamId`
}

export const getHttpEventStream = (tabId) => streams.get(keyOf(tabId))?.snapshot ?? EMPTY;
export const isHttpEventStreaming = (tabId) => getHttpEventStream(tabId).status === 'streaming';

const subscribe = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };

/** The tab's stream, live. */
export function useHttpEventStream(tabId) {
  return useSyncExternalStore(subscribe, () => getHttpEventStream(tabId));
}

/** Tests only. */
export function resetHttpEventStreamsForTest() {
  streams.clear();
  listeners.clear();
  counter = 0;
}
